import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Button, Card, message, Modal } from "antd";
import { CheckOutlined, StarOutlined } from "@ant-design/icons";
import { gql, useMutation } from "@apollo/client";
import { Container } from "./PricingStyle";

const CREATE_CHECKOUT_SESSION = gql`
  mutation createCheckoutSession($input: CreateCheckoutSessionInput!) {
    createCheckoutSession(input: $input) {
      sessionId
      url
    }
  }
`;

const PLANS = {
  monthly: {
    id: "monthly",
    name: "Monthly",
    price: 20,
    billing: "month",
    originalPrice: null,
    discount: null,
    trialDays: 7,
    popular: false,
  },
  yearly: {
    id: "yearly",
    name: "Yearly",
    price: 180,
    billing: "year",
    originalPrice: 240,
    discount: "25% off",
    trialDays: 21,
    popular: true,
  },
};

const FEATURES = [
  "Limited-time offer",
  "AI-native creative tools",
  "Unlimited content creation",
  "Full access to dashboard",
  "Priority customer support",
];

/** `trial`: free-trial UX. `payment`: checkout / renew copy (past_due, unpaid, etc.). */
export default function PricingPage({
  isModal = false,
  isRefreshing = false,
  checkoutPromptKind = "trial",
}) {
  const isTrialPrompt = checkoutPromptKind === "trial";
  // eslint-disable-next-line no-unused-vars
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const userInfo = useSelector((state) => state.userInfo.userInfo);
  const subscriptionStatus = userInfo?.subscriptionStatus;
  const [createCheckoutSession] = useMutation(CREATE_CHECKOUT_SESSION);

  const handleSubscribe = async (planId) => {
    setLoading(true);
    try {
      // Calculate trial and subscription dates
      const plan = PLANS[planId];
      const trialStartDate = new Date();
      const trialEndDate = new Date();
      trialEndDate.setDate(trialEndDate.getDate() + plan.trialDays);

      // Calculate billing date based on existing subscription or trial end
      let billingStartDate = new Date(trialEndDate);

      // If user has active subscription, new subscription starts from current period end
      if (subscriptionStatus?.currentPeriodEnd) {
        const currentEnd = new Date(subscriptionStatus.currentPeriodEnd);
        // If current subscription extends beyond trial, billing starts after it
        if (currentEnd > trialEndDate) {
          billingStartDate = currentEnd;
        }
      }

      const billingEndDate = new Date(billingStartDate);
      if (planId === "monthly") {
        billingEndDate.setMonth(billingEndDate.getMonth() + 1);
      } else if (planId === "yearly") {
        billingEndDate.setFullYear(billingEndDate.getFullYear() + 1);
      }

      const formatDate = (date) => {
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        });
      };

      const hasActiveSubscription = subscriptionStatus?.hasActiveSubscription;

      const confirmTitle = isTrialPrompt
        ? "Start Your Free Trial"
        : "Continue to checkout";
      const confirmOkText = isTrialPrompt
        ? "Start Free Trial"
        : "Continue to payment";

      const confirmContent = (
        <div>
          <p style={{ fontSize: "16px", marginBottom: "16px" }}>
            <strong>Plan:</strong> {plan.name} - ${plan.price}/{plan.billing}
          </p>

          {isTrialPrompt ? (
            <div
              style={{
                background: "#e6f7ff",
                padding: "16px",
                borderRadius: "8px",
                marginBottom: "12px",
                border: "1px solid #91d5ff",
              }}
            >
              <p
                style={{
                  margin: "0 0 8px 0",
                  fontWeight: "bold",
                  color: "#1890ff",
                }}
              >
                ✨ Free Trial Period
              </p>
              <p style={{ margin: "4px 0" }}>
                <strong>Trial Start:</strong> {formatDate(trialStartDate)}
              </p>
              <p style={{ margin: "4px 0" }}>
                <strong>Trial End:</strong> {formatDate(trialEndDate)}
              </p>
            </div>
          ) : null}

          <div
            style={{
              background: "#f5f5f5",
              padding: "16px",
              borderRadius: "8px",
              marginBottom: "16px",
            }}
          >
            <p style={{ margin: "0 0 8px 0", fontWeight: "bold" }}>
              💳 Billing
            </p>
            <p style={{ margin: "4px 0" }}>
              <strong>First charge:</strong> {formatDate(billingStartDate)}
            </p>
            <p style={{ margin: "4px 0" }}>
              <strong>Next billing:</strong> {formatDate(billingEndDate)}
            </p>
          </div>

          <p style={{ color: "#666", fontSize: "14px" }}>
            {hasActiveSubscription
              ? "You already have subscription activity on this account. The new plan timing follows Stripe and your current period where applicable."
              : isTrialPrompt
                ? "You'll get full access immediately. Your card will be charged after the trial ends. Cancel anytime during the trial period with no charges."
                : "You'll continue to Stripe to enter payment details and activate your subscription. Charges follow the plan above."}
          </p>
        </div>
      );

      Modal.confirm({
        title: confirmTitle,
        content: confirmContent,
        okText: confirmOkText,
        cancelText: "Cancel",
        onOk: async () => {
          let loadingMsg = null;
          try {
            // Show loading message
            loadingMsg = message.loading("Creating payment session...", 0);

            // Call createCheckoutSession API
            const response = await createCheckoutSession({
              variables: {
                input: {
                  planType: planId.toUpperCase(), // Convert "monthly" to "MONTHLY"
                },
              },
            });

            loadingMsg();
            loadingMsg = null;

            const checkoutUrl = response?.data?.createCheckoutSession?.url;

            if (checkoutUrl) {
              message.success("Redirecting to payment page...");
              // Redirect to Stripe Checkout page
              window.location.href = checkoutUrl;
            } else {
              throw new Error("Failed to get checkout URL");
            }
          } catch (error) {
            // Close loading message if still open
            if (loadingMsg) {
              loadingMsg();
            }
            message.error(
              error?.message ||
              "Failed to create payment session. Please try again."
            );
            setLoading(false);
          }
        },
        onCancel: () => {
          setLoading(false);
        },
      });
    } catch (error) {
      message.error("Subscription failed, please try again later");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container style={isModal ? { padding: "32px 24px" } : {}}>
      <div className="pricing-header">
        <h1
          className="pricing-title"
          style={isModal ? { fontSize: "28px" } : {}}
        >
          {!isModal
            ? "Start your free trial to unlock full access"
            : isTrialPrompt
              ? "Upgrade to continue publishing"
              : "Subscribe to continue publishing"}
        </h1>
        <p className="pricing-subtitle">
          {isRefreshing
            ? "Processing your subscription..."
            : isModal && !isTrialPrompt
              ? "Choose a plan and continue to Stripe to complete payment."
              : "No charges today. Cancel anytime."}
        </p>
      </div>

      <div className="pricing-cards">
        {Object.values(PLANS).map((plan) => (
          <Card
            key={plan.id}
            className={`pricing-card ${plan.popular ? "popular" : ""}`}
            hoverable
          >
            {plan.popular && (
              <div className="popular-badge">
                <StarOutlined /> Most Popular
              </div>
            )}
            <div className="plan-header">
              <h2 className="plan-name">{plan.name}</h2>
              <div className="plan-price">
                <span className="price-amount">${plan.price}</span>
                <span className="price-billing">/{plan.billing}</span>
              </div>
              {plan.originalPrice && (
                <div className="plan-discount">
                  <span className="original-price">
                    ${plan.originalPrice}/year
                  </span>
                  <span className="discount-badge">{plan.discount}</span>
                </div>
              )}
              <div className="trial-info">
                {isTrialPrompt
                  ? `${plan.trialDays} days free trial`
                  : "Secure checkout"}
              </div>
            </div>

            <div className="plan-features">
              {FEATURES.map((feature, index) => (
                <div key={index} className="feature-item">
                  <CheckOutlined className="check-icon" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>

            <Button
              type={plan.popular ? "primary" : "default"}
              size="large"
              block
              className="subscribe-btn"
              loading={loading || isRefreshing}
              onClick={() => handleSubscribe(plan.id)}
              disabled={isRefreshing}
            >
              {isRefreshing
                ? "Processing..."
                : isTrialPrompt
                  ? "Start Free Trial"
                  : "Continue to checkout"}
            </Button>
          </Card>
        ))}
      </div>

      <div className="pricing-footer">
        <p>
          {isTrialPrompt
            ? "All plans include free trial. You won't be charged until the trial ends. Cancel anytime during the trial period with no charges."
            : "You will be redirected to Stripe to complete payment securely."}
        </p>
      </div>
    </Container>
  );
}
